import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";

// Standard normal CDF (Abramowitz & Stegun 7.1.26).
function normCdf(x: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989422804014327 * Math.exp(-x * x / 2);
  const p = d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return x >= 0 ? 1 - p : p;
}

// Black-Scholes theta, per share PER DAY (negative for a long option).
function bsThetaPerDay(S: number, K: number, tYears: number, sigma: number, r: number, isPut: boolean): number | null {
  if (!(S > 0) || !(K > 0) || !(tYears > 0) || !(sigma > 0)) return null;
  const sqrtT = Math.sqrt(tYears);
  const d1 = (Math.log(S / K) + (r + (sigma * sigma) / 2) * tYears) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  const pdf = 0.3989422804014327 * Math.exp(-d1 * d1 / 2);
  const term1 = -(S * sigma * pdf) / (2 * sqrtT);
  const perYear = isPut
    ? term1 + r * K * Math.exp(-r * tYears) * normCdf(-d2)
    : term1 - r * K * Math.exp(-r * tYears) * normCdf(d2);
  return perYear / 365;
}

// Black-Scholes option price (per share).
function bsPrice(S: number, K: number, T: number, sigma: number, r: number, isPut: boolean): number {
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + (sigma * sigma) / 2) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  return isPut
    ? K * Math.exp(-r * T) * normCdf(-d2) - S * normCdf(-d1)
    : S * normCdf(d1) - K * Math.exp(-r * T) * normCdf(d2);
}

// Implied vol from an option price (bisection). null if no solution.
function impliedVol(S: number, K: number, T: number, r: number, isPut: boolean, price: number): number | null {
  if (!(S > 0) || !(K > 0) || !(T > 0) || !(price > 0)) return null;
  let lo = 1e-4, hi = 5;
  for (let i = 0; i < 100; i++) {
    const m = (lo + hi) / 2;
    if (bsPrice(S, K, T, m, r, isPut) > price) hi = m;
    else lo = m;
  }
  return (lo + hi) / 2;
}

// Net position theta of the credit spread ($/day): long the long leg, short
// the short leg. IV is implied from the short-leg price (then applied to both
// legs) so the result is reproducible from the displayed inputs. Returns
// null when inputs are insufficient.
export function netSpreadTheta(
  spot: number | null | undefined,
  shortStrike: number,
  longStrike: number,
  shortPx: number | null | undefined,
  dteDays: number,
  isPut: boolean,
  contracts: number,
): number | null {
  if (spot == null || !(spot > 0) || shortPx == null || !(shortPx > 0) || !(dteDays > 0)) return null;
  const T = dteDays / 365;
  const r = 0.04; // not stored per trade; theta is weakly sensitive to r
  const sigma = impliedVol(spot, shortStrike, T, r, isPut, shortPx);
  if (sigma == null) return null;
  const thShort = bsThetaPerDay(spot, shortStrike, T, sigma, r, isPut);
  const thLong = bsThetaPerDay(spot, longStrike, T, sigma, r, isPut);
  if (thShort == null || thLong == null) return null;
  return (thLong - thShort) * OPTION_MULTIPLIER * contracts; // +1 long, -1 short
}
